import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Popconfirm, Modal, Form, Input, InputNumber, Switch, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import apiService from '@/services/api.service';
import type { ApiResponse } from '@/types/api';
import { logger } from '@/utils/logger';
import { API_ENDPOINTS } from '@/constants/api.constants';

interface PromoCode {
    id: number;
    code: string;
    name: string | null;
    discountType: 'percent' | 'fixed';
    discountValue: number;
    validFrom: string | null;
    validUntil: string | null;
    maxUses: number | null;
    usedCount: number;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

const PromoCodesPage: React.FC = () => {
    const [list, setList] = useState<PromoCode[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editing, setEditing] = useState<PromoCode | null>(null);
    const [form] = Form.useForm();

    const fetchList = async () => {
        try {
            setLoading(true);
            const res = await apiService.get<ApiResponse<PromoCode[]>>(API_ENDPOINTS.PROMO_CODES.BASE);
            setList(Array.isArray(res.data?.data) ? res.data.data : []);
        } catch (e) {
            logger.error('Error fetching promo codes', e);
            message.error('Ошибка загрузки промокодов');
            setList([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchList(); }, []);

    const handleCreate = () => {
        setEditing(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (row: PromoCode) => {
        setEditing(row);
        form.setFieldsValue({
            code: row.code,
            name: row.name,
            discountType: row.discountType,
            discountValue: parseFloat(String(row.discountValue)),
            validFrom: row.validFrom || undefined,
            validUntil: row.validUntil || undefined,
            maxUses: row.maxUses ?? undefined,
            isActive: row.isActive
        });
        setModalVisible(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const body = {
                code: values.code?.trim?.() || values.code,
                name: values.name || null,
                discountType: values.discountType || 'percent',
                discountValue: Number(values.discountValue),
                validFrom: values.validFrom || null,
                validUntil: values.validUntil || null,
                maxUses: values.maxUses != null ? Number(values.maxUses) : null,
                isActive: values.isActive !== false
            };
            if (editing) {
                await apiService.put(API_ENDPOINTS.PROMO_CODES.UPDATE(editing.id), body);
                message.success('Промокод обновлён');
            } else {
                await apiService.post(API_ENDPOINTS.PROMO_CODES.CREATE, body);
                message.success('Промокод создан');
            }
            setModalVisible(false);
            fetchList();
        } catch (err: any) {
            const msg = err?.response?.data?.message || 'Ошибка сохранения';
            message.error(msg);
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await apiService.delete(API_ENDPOINTS.PROMO_CODES.DELETE(id));
            message.success('Промокод удалён');
            fetchList();
        } catch (e: any) {
            message.error(e?.response?.data?.message || 'Ошибка удаления');
        }
    };

    const columns: ColumnsType<PromoCode> = [
        { title: 'Код', dataIndex: 'code', key: 'code', render: (c: string) => <strong>{c}</strong> },
        { title: 'Название', dataIndex: 'name', key: 'name' },
        {
            title: 'Скидка',
            key: 'discount',
            render: (_, r) => r.discountType === 'percent' ? `${r.discountValue}%` : `${parseFloat(String(r.discountValue))} ₽`
        },
        { title: 'Использован', dataIndex: 'usedCount', key: 'usedCount', width: 90 },
        { title: 'Макс. использований', dataIndex: 'maxUses', key: 'maxUses', render: (v: number | null) => v ?? '—' },
        { title: 'Действует с', dataIndex: 'validFrom', key: 'validFrom', render: (v: string | null) => v || '—' },
        { title: 'Действует до', dataIndex: 'validUntil', key: 'validUntil', render: (v: string | null) => v || '—' },
        { title: 'Активен', dataIndex: 'isActive', key: 'isActive', render: (v: boolean) => (v ? 'Да' : 'Нет') },
        {
            title: 'Действия',
            key: 'actions',
            width: 120,
            render: (_, row) => (
                <Space>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(row)} />
                    <Popconfirm title="Удалить промокод?" onConfirm={() => handleDelete(row.id)} okText="Да" cancelText="Нет">
                        <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h1>Промокоды</h1>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>Добавить промокод</Button>
            </div>
            <Table
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={list}
                pagination={{ pageSize: 20 }}
            />
            <Modal
                title={editing ? 'Редактировать промокод' : 'Новый промокод'}
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
                okText="Сохранить"
                cancelText="Отмена"
                width={520}
            >
                <Form form={form} layout="vertical" initialValues={{ discountType: 'percent', isActive: true }}>
                    <Form.Item name="code" label="Код" rules={[{ required: true, message: 'Введите код' }]}>
                        <Input placeholder="WELCOME10" />
                    </Form.Item>
                    <Form.Item name="name" label="Название (для админки)">
                        <Input placeholder="Скидка 10%" />
                    </Form.Item>
                    <Form.Item name="discountType" label="Тип скидки" rules={[{ required: true }]}>
                        <Select options={[
                            { value: 'percent', label: 'Процент (%)' },
                            { value: 'fixed', label: 'Фиксированная сумма (₽)' }
                        ]} />
                    </Form.Item>
                    <Form.Item name="discountValue" label="Значение скидки" rules={[{ required: true, message: 'Укажите значение' }]}>
                        <InputNumber min={0} step={1} style={{ width: '100%' }} placeholder="10 или 500" />
                    </Form.Item>
                    <Form.Item name="validFrom" label="Действует с (дата)">
                        <Input type="date" />
                    </Form.Item>
                    <Form.Item name="validUntil" label="Действует до (дата)">
                        <Input type="date" />
                    </Form.Item>
                    <Form.Item name="maxUses" label="Максимум использований (пусто — без ограничений)">
                        <InputNumber min={1} style={{ width: '100%' }} placeholder="Пусто" />
                    </Form.Item>
                    <Form.Item name="isActive" label="Активен" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default PromoCodesPage;
